import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Package, Plus, Trash2 } from 'lucide-react';

const CreateOrder = ({ user, onLogout }) => {
  const [dispensaries, setDispensaries] = useState([]);
  const [orderData, setOrderData] = useState({
    dispensary_id: '',
    customer: {
      name: '',
      phone: '',
      age_verified: true
    },
    items: [
      { sku: '', name: '', quantity: 1, notes: '' }
    ],
    mode: 'STANDARD',
    delivery_address: {
      line1: '',
      city: 'Guadalajara',
      state: 'Jalisco',
      postal_code: '',
      lat: 20.6597,
      lng: -103.3496,
      notes: ''
    },
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const fetchDispensaries = useCallback(async () => {
    try {
      const response = await axios.get('/dispensaries');
      setDispensaries(response.data);
      if (response.data.length > 0) {
        setOrderData(prev => ({
          ...prev,
          dispensary_id: prev.dispensary_id || response.data[0].id
        }));
      }
    } catch (error) {
      toast.error('Error al cargar dispensarios');
    }
  }, []);

  useEffect(() => {
    fetchDispensaries();
  }, [fetchDispensaries]);

  const handleAddItem = useCallback(() => {
    setOrderData(prev => ({
      ...prev,
      items: [...prev.items, { sku: '', name: '', quantity: 1, notes: '' }]
    }));
  }, []);

  const handleRemoveItem = useCallback((index) => {
    setOrderData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  }, []);

  const handleItemChange = useCallback((index, field, value) => {
    setOrderData(prev => {
      const newItems = [...prev.items];
      newItems[index][field] = value;
      return { ...prev, items: newItems };
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/orders', orderData);
      toast.success('¡Pedido creado exitosamente!');
      window.location.href = `/orders/${response.data.id}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear pedido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} title="Crear Pedido">
      <div className="max-w-4xl mx-auto" data-testid="create-order-container">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl" style={{ color: '#224049' }}>Nuevo Pedido</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Dispensary Selection */}
              <div className="space-y-2">
                <Label>Dispensario</Label>
                <Select
                  value={orderData.dispensary_id}
                  onValueChange={(value) => setOrderData({ ...orderData, dispensary_id: value })}
                >
                  <SelectTrigger data-testid="select-dispensary">
                    <SelectValue placeholder="Selecciona un dispensario" />
                  </SelectTrigger>
                  <SelectContent>
                    {dispensaries.map((disp) => (
                      <SelectItem key={disp.id} value={disp.id}>
                        {disp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Delivery Mode */}
              <div className="space-y-2">
                <Label>Modalidad de Entrega</Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    className={`flex-1 p-4 rounded-lg border-2 font-semibold ${
                      orderData.mode === 'STANDARD' ? 'border-current' : 'border-gray-300'
                    }`}
                    style={{
                      backgroundColor: orderData.mode === 'STANDARD' ? '#F0EBE3' : 'white',
                      color: orderData.mode === 'STANDARD' ? '#224049' : '#666'
                    }}
                    onClick={() => setOrderData({ ...orderData, mode: 'STANDARD' })}
                    data-testid="mode-standard-button"
                  >
                    STANDARD<br />
                    <span className="text-sm font-normal">2-6 horas</span>
                  </button>
                  <button
                    type="button"
                    className={`flex-1 p-4 rounded-lg border-2 font-semibold ${
                      orderData.mode === 'EXPRESS' ? 'border-current' : 'border-gray-300'
                    }`}
                    style={{
                      background: orderData.mode === 'EXPRESS' ? 'linear-gradient(135deg, #D4AD6A, #D9A27F)' : 'white',
                      color: orderData.mode === 'EXPRESS' ? 'white' : '#666'
                    }}
                    onClick={() => setOrderData({ ...orderData, mode: 'EXPRESS' })}
                    data-testid="mode-express-button"
                  >
                    EXPRESS 60'<br />
                    <span className="text-sm font-normal">≤60 min</span>
                  </button>
                </div>
              </div>

              {/* Customer Info */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#EAE0D5' }}>
                <h3 className="font-semibold mb-3" style={{ color: '#224049' }}>Información del Cliente</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer-name">Nombre Completo</Label>
                    <Input
                      id="customer-name"
                      data-testid="customer-name-input"
                      value={orderData.customer.name}
                      onChange={(e) => setOrderData({
                        ...orderData,
                        customer: { ...orderData.customer, name: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer-phone">Teléfono</Label>
                    <Input
                      id="customer-phone"
                      data-testid="customer-phone-input"
                      type="tel"
                      value={orderData.customer.phone}
                      onChange={(e) => setOrderData({
                        ...orderData,
                        customer: { ...orderData.customer, phone: e.target.value }
                      })}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#EAE0D5' }}>
                <h3 className="font-semibold mb-3" style={{ color: '#224049' }}>Dirección de Entrega</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="address-line1">Calle y Número</Label>
                    <Input
                      id="address-line1"
                      data-testid="address-line1-input"
                      value={orderData.delivery_address.line1}
                      onChange={(e) => setOrderData({
                        ...orderData,
                        delivery_address: { ...orderData.delivery_address, line1: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="address-city">Ciudad</Label>
                      <Input
                        id="address-city"
                        data-testid="address-city-input"
                        value={orderData.delivery_address.city}
                        onChange={(e) => setOrderData({
                          ...orderData,
                          delivery_address: { ...orderData.delivery_address, city: e.target.value }
                        })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address-state">Estado</Label>
                      <Input
                        id="address-state"
                        data-testid="address-state-input"
                        value={orderData.delivery_address.state}
                        onChange={(e) => setOrderData({
                          ...orderData,
                          delivery_address: { ...orderData.delivery_address, state: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address-postal">Código Postal</Label>
                      <Input
                        id="address-postal"
                        data-testid="address-postal-input"
                        value={orderData.delivery_address.postal_code}
                        onChange={(e) => setOrderData({
                          ...orderData,
                          delivery_address: { ...orderData.delivery_address, postal_code: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address-notes">Notas de Dirección</Label>
                    <Input
                      id="address-notes"
                      data-testid="address-notes-input"
                      placeholder="Ej: Puerta 3, Timbre 2B"
                      value={orderData.delivery_address.notes}
                      onChange={(e) => setOrderData({
                        ...orderData,
                        delivery_address: { ...orderData.delivery_address, notes: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#EAE0D5' }}>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold" style={{ color: '#224049' }}>Artículos</h3>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddItem}
                    style={{ backgroundColor: '#D9A27F', color: 'white' }}
                    data-testid="add-item-button"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Agregar
                  </Button>
                </div>
                <div className="space-y-4">
                  {orderData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 bg-white rounded-lg" data-testid={`item-row-${index}`}>
                      <Input
                        placeholder="SKU"
                        value={item.sku}
                        onChange={(e) => handleItemChange(index, 'sku', e.target.value)}
                        data-testid={`item-sku-input-${index}`}
                        required
                      />
                      <Input
                        placeholder="Nombre del Producto"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                        data-testid={`item-name-input-${index}`}
                        required
                      />
                      <Input
                        type="number"
                        min="1"
                        placeholder="Cantidad"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                        data-testid={`item-quantity-input-${index}`}
                        required
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                        disabled={orderData.items.length === 1}
                        data-testid={`remove-item-button-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="order-notes">Notas del Pedido (Opcional)</Label>
                <Input
                  id="order-notes"
                  data-testid="order-notes-input"
                  placeholder="Instrucciones especiales..."
                  value={orderData.notes}
                  onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
                />
              </div>

              {/* Submit */}
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.location.href = '/orders'}
                  className="flex-1"
                  data-testid="cancel-create-order-button"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  style={{ backgroundColor: '#224049', color: '#D4AD6A' }}
                  disabled={loading}
                  data-testid="submit-create-order-button"
                >
                  {loading ? 'Creando...' : 'Crear Pedido'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CreateOrder;